import React, { useEffect, useState } from 'react';
import { DollarSignIcon, PlusIcon, FilterIcon, BookIcon, TagIcon, SettingsIcon } from 'lucide-react';
import { BudgetList } from './BudgetList';
import { BudgetForm } from './BudgetForm';
import { BudgetTypes } from './BudgetTypes';
export type BudgetType = {
  id: string;
  name: string;
  description: string;
};
export type Budget = {
  id: string;
  name: string;
  description: string;
  budgetTypeId: string;
  budgetTypeName: string;
  glCode: string;
  entityId?: string;
  entityName?: string;
  siteId?: string;
  siteName?: string;
  totalAmount: number;
  remainingAmount: number;
  currency: string;
  startDate: string;
  endDate: string;
  status: 'Active' | 'Archived';
  rules: string; // JSON string
  createdAt: string;
  updatedAt: string;
};
export function BudgetManagement({
  locale = 'en-US'
}) {
  const [activeView, setActiveView] = useState<'list' | 'create' | 'edit' | 'types'>('list');
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [budgetTypes, setBudgetTypes] = useState<BudgetType[]>([]);
  const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null);
  const [filters, setFilters] = useState({
    budgetType: '',
    entity: '',
    site: '',
    glCode: '',
    status: ''
  });
  // Load sample data
  useEffect(() => {
    // In a real app, this would be API calls
    const sampleBudgetTypes: BudgetType[] = [{
      id: 'type-001',
      name: 'Operational',
      description: 'Regular operational expenses'
    }, {
      id: 'type-002',
      name: 'Project',
      description: 'Project-specific budgets'
    }, {
      id: 'type-003',
      name: 'Capital',
      description: 'Capital expenditure budgets'
    }, {
      id: 'type-004',
      name: 'Marketing',
      description: 'Marketing and advertising budgets'
    }];
    const sampleBudgets: Budget[] = [{
      id: 'budget-001',
      name: 'Office Supplies Q1 2023',
      description: 'Budget for office supplies for Q1 2023',
      budgetTypeId: 'type-001',
      budgetTypeName: 'Operational',
      glCode: '6100.100',
      entityId: 'entity-001',
      entityName: 'Corporate',
      siteId: 'site-001',
      siteName: 'Headquarters',
      totalAmount: 50000,
      remainingAmount: 32450,
      currency: 'USD',
      startDate: '2023-01-01',
      endDate: '2023-03-31',
      status: 'Active',
      rules: JSON.stringify({
        maxPerTransaction: 5000,
        warningThreshold: 0.8,
        vendorBlacklist: ['Vendor X', 'Vendor Y']
      }),
      createdAt: '2022-12-15T10:00:00Z',
      updatedAt: '2023-01-10T14:30:00Z'
    }, {
      id: 'budget-002',
      name: 'IT Equipment 2023',
      description: 'Annual budget for IT equipment purchases',
      budgetTypeId: 'type-001',
      budgetTypeName: 'Operational',
      glCode: '6200.200',
      entityId: 'entity-001',
      entityName: 'Corporate',
      totalAmount: 120000,
      remainingAmount: 85600,
      currency: 'USD',
      startDate: '2023-01-01',
      endDate: '2023-12-31',
      status: 'Active',
      rules: JSON.stringify({
        maxPerTransaction: 10000,
        approvalRequired: true,
        warningThreshold: 0.7
      }),
      createdAt: '2022-12-01T09:00:00Z',
      updatedAt: '2023-02-15T11:20:00Z'
    }, {
      id: 'budget-003',
      name: 'Website Redesign Project',
      description: 'Budget for website redesign project',
      budgetTypeId: 'type-002',
      budgetTypeName: 'Project',
      glCode: '6300.300',
      entityId: 'entity-001',
      entityName: 'Corporate',
      totalAmount: 75000,
      remainingAmount: 15000,
      currency: 'USD',
      startDate: '2023-02-01',
      endDate: '2023-06-30',
      status: 'Active',
      rules: JSON.stringify({
        projectCode: 'WEB-2023',
        maxPerTransaction: 15000
      }),
      createdAt: '2023-01-20T14:00:00Z',
      updatedAt: '2023-03-05T16:45:00Z'
    }, {
      id: 'budget-004',
      name: 'Marketing Campaign Q2',
      description: 'Q2 marketing campaign budget',
      budgetTypeId: 'type-004',
      budgetTypeName: 'Marketing',
      glCode: '6400.100',
      entityId: 'entity-002',
      entityName: 'LATAM Operations',
      siteId: 'site-004',
      siteName: 'Mexico City',
      totalAmount: 60000,
      remainingAmount: 60000,
      currency: 'USD',
      startDate: '2023-04-01',
      endDate: '2023-06-30',
      status: 'Active',
      rules: JSON.stringify({
        campaignCode: 'MKT-Q2-2023',
        maxPerTransaction: 8000
      }),
      createdAt: '2023-03-15T10:30:00Z',
      updatedAt: '2023-03-15T10:30:00Z'
    }, {
      id: 'budget-005',
      name: 'Office Renovation',
      description: 'Office renovation project budget',
      budgetTypeId: 'type-003',
      budgetTypeName: 'Capital',
      glCode: '1500.100',
      entityId: 'entity-003',
      entityName: 'APAC Division',
      siteId: 'site-006',
      siteName: 'Singapore',
      totalAmount: 250000,
      remainingAmount: 125000,
      currency: 'SGD',
      startDate: '2023-01-01',
      endDate: '2023-12-31',
      status: 'Active',
      rules: JSON.stringify({
        projectCode: 'RENO-2023',
        maxPerTransaction: 50000,
        approvalRequired: true
      }),
      createdAt: '2022-11-10T08:15:00Z',
      updatedAt: '2023-02-20T09:45:00Z'
    }, {
      id: 'budget-006',
      name: 'Training Program 2022',
      description: 'Employee training program budget',
      budgetTypeId: 'type-001',
      budgetTypeName: 'Operational',
      glCode: '6500.300',
      entityId: 'entity-001',
      entityName: 'Corporate',
      totalAmount: 35000,
      remainingAmount: 2500,
      currency: 'USD',
      startDate: '2022-01-01',
      endDate: '2022-12-31',
      status: 'Archived',
      rules: JSON.stringify({
        maxPerTransaction: 5000
      }),
      createdAt: '2021-12-05T11:30:00Z',
      updatedAt: '2023-01-05T10:00:00Z'
    }];
    setBudgetTypes(sampleBudgetTypes);
    setBudgets(sampleBudgets);
  }, []);
  const handleCreateBudget = (budget: Omit<Budget, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newBudget: Budget = {
      ...budget,
      id: `budget-${budgets.length + 1}`.padStart(10, '0'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setBudgets([...budgets, newBudget]);
    setActiveView('list');
  };
  const handleUpdateBudget = (updatedBudget: Budget) => {
    setBudgets(budgets.map(budget => budget.id === updatedBudget.id ? {
      ...updatedBudget,
      updatedAt: new Date().toISOString()
    } : budget));
    setActiveView('list');
    setSelectedBudget(null);
  };
  const handleArchiveBudget = (budgetId: string) => {
    setBudgets(budgets.map(budget => budget.id === budgetId ? {
      ...budget,
      status: 'Archived',
      updatedAt: new Date().toISOString()
    } : budget));
  };
  const handleRestoreBudget = (budgetId: string) => {
    setBudgets(budgets.map(budget => budget.id === budgetId ? {
      ...budget,
      status: 'Active',
      updatedAt: new Date().toISOString()
    } : budget));
  };
  const handleCreateBudgetType = (budgetType: Omit<BudgetType, 'id'>) => {
    const newBudgetType: BudgetType = {
      ...budgetType,
      id: `type-${budgetTypes.length + 1}`.padStart(6, '0')
    };
    setBudgetTypes([...budgetTypes, newBudgetType]);
  };
  const handleUpdateBudgetType = (updatedBudgetType: BudgetType) => {
    setBudgetTypes(budgetTypes.map(budgetType => budgetType.id === updatedBudgetType.id ? updatedBudgetType : budgetType));
  };
  const handleDeleteBudgetType = (budgetTypeId: string) => {
    // Check if any budgets are using this type
    const budgetsUsingType = budgets.some(budget => budget.budgetTypeId === budgetTypeId);
    if (budgetsUsingType) {
      alert('Cannot delete budget type that is in use by existing budgets.');
      return;
    }
    setBudgetTypes(budgetTypes.filter(budgetType => budgetType.id !== budgetTypeId));
  };
  const handleEditBudget = (budget: Budget) => {
    setSelectedBudget(budget);
    setActiveView('edit');
  };
  const handleFilterChange = (name: string, value: string) => {
    setFilters({
      ...filters,
      [name]: value
    });
  };
  const filteredBudgets = budgets.filter(budget => {
    return (filters.budgetType === '' || budget.budgetTypeId === filters.budgetType) && (filters.entity === '' || budget.entityId === filters.entity) && (filters.site === '' || budget.siteId === filters.site) && (filters.glCode === '' || budget.glCode.toLowerCase().includes(filters.glCode.toLowerCase())) && (filters.status === '' || budget.status === filters.status);
  });
  return <div className="space-y-6" data-id="element-6783">
      <div className="mb-8" data-id="element-6784">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-6785">Budget Management</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-6786">
          Create and manage budgets for your organization
        </p>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-6787">
        <div className="border-b border-gray-200" data-id="element-6788">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs" data-id="element-6789">
            <button onClick={() => setActiveView('list')} className={`${activeView === 'list' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-6790">
              <DollarSignIcon className="h-4 w-4 inline mr-2" data-id="element-6791" />
              Budgets
            </button>
            <button onClick={() => setActiveView('types')} className={`${activeView === 'types' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-6792">
              <TagIcon className="h-4 w-4 inline mr-2" data-id="element-6793" />
              Budget Types
            </button>
          </nav>
        </div>
        <div className="p-6" data-id="element-6794">
          {activeView === 'list' && <>
              <div className="flex justify-between mb-6" data-id="element-6795">
                <div className="flex items-center space-x-4" data-id="element-6796">
                  <div className="relative" data-id="element-6797">
                    <select id="budgetType" name="budgetType" className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.budgetType} onChange={e => handleFilterChange('budgetType', e.target.value)} data-id="element-6798">
                      <option value="" data-id="element-6799">All Budget Types</option>
                      {budgetTypes.map(type => <option key={type.id} value={type.id} data-id="element-6800">
                          {type.name}
                        </option>)}
                    </select>
                  </div>
                  <div className="relative" data-id="element-6801">
                    <select id="status" name="status" className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} data-id="element-6802">
                      <option value="" data-id="element-6803">All Statuses</option>
                      <option value="Active" data-id="element-6804">Active</option>
                      <option value="Archived" data-id="element-6805">Archived</option>
                    </select>
                  </div>
                  <div className="relative" data-id="element-6806">
                    <input type="text" name="glCode" id="glCode" placeholder="Filter by GL Code" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-48 sm:text-sm border-gray-300 rounded-md" value={filters.glCode} onChange={e => handleFilterChange('glCode', e.target.value)} data-id="element-6807" />
                  </div>
                  <div className="relative" data-id="element-6808">
                    <FilterIcon className="h-5 w-5 text-gray-400" data-id="element-6809" />
                  </div>
                </div>
                <button type="button" onClick={() => setActiveView('create')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-6810">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-6811" />
                  Create Budget
                </button>
              </div>
              <BudgetList budgets={filteredBudgets} onEdit={handleEditBudget} onArchive={handleArchiveBudget} onRestore={handleRestoreBudget} locale={locale} data-id="element-6812" />
            </>}
          {activeView === 'create' && <BudgetForm budgetTypes={budgetTypes} onSave={handleCreateBudget} onCancel={() => setActiveView('list')} locale={locale} data-id="element-6813" />}
          {activeView === 'edit' && selectedBudget && <BudgetForm budget={selectedBudget} budgetTypes={budgetTypes} onSave={handleUpdateBudget} onCancel={() => {
          setActiveView('list');
          setSelectedBudget(null);
        }} locale={locale} data-id="element-6814" />}
          {activeView === 'types' && <BudgetTypes budgetTypes={budgetTypes} onCreateBudgetType={handleCreateBudgetType} onUpdateBudgetType={handleUpdateBudgetType} onDeleteBudgetType={handleDeleteBudgetType} locale={locale} data-id="element-6815" />}
        </div>
      </div>
    </div>;
}